import { USERS } from '../types'
import { handleError } from '../../utils/error'
import { STATUS } from '../types'
import { changeStatus } from './status'
import Api from '../../api'
import { userSetups } from './userSetups'
import { loggedUser } from './user'
import { loadStateFromLocalStorage } from '../utilsLocalStorage'

// Make this function async and fetch setups from API
const transformSetupsId2Name = async setupsArr => {
  const type = typeof setupsArr[0]

  if (type === 'number') {
    const response = await Api.Setups.getAllSetups()
    const setupsApi = response.data.setups
    return setupsApi
      .filter(setup => setupsArr.includes(setup.id))
      .map(setup => setup.name)
  } else {
    return setupsArr
  }
}

// Make this function async and fetch setups from API
const transformSetupsName2Id = async setupsToTransform => {
  const type = typeof setupsToTransform[0]

  if (type === 'string') {
    const response = await Api.Setups.getAllSetups()
    const setupsApi = response.data.setups
    return setupsApi
      .filter(setup => setupsToTransform.includes(setup.name))
      .map(setup => setup.id)
  } else {
    return setupsToTransform
  }
}

export const getAllUsers = () => {
  return dispatch => {
    return Api.Users.getAllUsers()
      .then(res => {
        dispatch({ type: USERS.LOADED, payload: res.data.users })
      })
      .catch(err => {
        handleError(dispatch, err, 'Failed to load users')
      })
  }
}

export const createUser = newUser => {
  return async (dispatch, getState) => {
    dispatch(changeStatus(STATUS.FETCHING))
    try {
      const res = await Api.Users.createUser(newUser)
      const transSetups = await transformSetupsId2Name(newUser.setups)
      const newUserWithId = {
        ...newUser,
        id: res.data.user_id,
        setups: transSetups,
      }

      const updatedUsers = [...getState().users, newUserWithId]

      dispatch({ type: USERS.CREATED, payload: updatedUsers })
      dispatch(changeStatus(STATUS.SUCCESS, res.data.msg))
    } catch (err) {
      handleError(dispatch, err, 'Create user failed')
    }
  }
}

export const deleteUser = (id, name) => {
  return (dispatch, getState) => {
    dispatch(changeStatus(STATUS.FETCHING))
    return Api.Users.deleteUser(id)
      .then(res => {
        const updatedUsers = getState().users.filter(user => user.id !== id)

        dispatch({ type: USERS.DELETED, payload: updatedUsers })
        dispatch(
          changeStatus(
            STATUS.SUCCESS,
            `User ${name} was successfully deleted.`,
          ),
        )
      })
      .catch(err => {
        handleError(dispatch, err, 'Delete user failed')
      })
  }
}

export const updateUser = updatedUser => {
  return async (dispatch, getState) => {
    try {
      dispatch(changeStatus(STATUS.FETCHING))

      const transformedSetups = await transformSetupsName2Id(updatedUser.setups)
      const { id, permissions, email, password, timezone, unit, enabled } =
        updatedUser

      const partialUser = {
        user_id: id,
        setups: transformedSetups,
        permissions,
        email,
        ...(password !== '' && { password }),
        timezone,
        unit,
        enabled,
      }

      const res = await Api.Users.updateUser(partialUser)

      if (res.status === 200) {
        const transSetups = await transformSetupsId2Name(partialUser.setups)
        const newUpdUser = { ...updatedUser, setups: transSetups }

        const userSetupsResponse = await Api.Setups.getUserSetups()
        const setupsFromUser = userSetupsResponse?.data?.setups

        if (!Array.isArray(setupsFromUser)) {
          console.error(
            `No user setups returned from API. Response: ${JSON.stringify(
              userSetupsResponse?.data?.setups,
            )}`,
          )
          throw new Error('No user setups returned from API.')
        }

        const enabledSetups = setupsFromUser.filter(s => s.enabled)
        const powerSetups = setupsFromUser.filter(
          s => s.enabled && s.is_power === true,
        )
        dispatch(userSetups(enabledSetups, powerSetups, newUpdUser.permissions))

        // Get the current logged user state
        const prevUserState = getState().user

        const mergedUser = {
          ...prevUserState,
          // Fallback to localStorage if Redux wasn't restored after refresh
          username:
            prevUserState.username || loadStateFromLocalStorage('username'),
          permissions: newUpdUser.permissions,
          setups: setupsFromUser,
          timezone: newUpdUser.timezone,
          unit: newUpdUser.unit,
          // Only overwrite password if a new one is provided
          ...(newUpdUser.password && { password: newUpdUser.password }),
        }
        dispatch(loggedUser(mergedUser))

        const updatedUsers = getState().users.map(user => {
          if (user.id === newUpdUser.id) {
            return { ...user, ...newUpdUser }
          }
          return user
        })

        dispatch({ type: USERS.UPDATED, payload: updatedUsers })
        dispatch(
          changeStatus(
            STATUS.SUCCESS,
            `The status of ${newUpdUser.name} was successfully updated.`,
          ),
        )
      } else {
        dispatch(changeStatus(STATUS.ERROR, 'Update failed'))
      }
    } catch (err) {
      handleError(dispatch, err, 'Update user failed')
    }
  }
}
